import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { ImagePlus, X } from "lucide-react";
import { cn } from "../lib/utils";
import type { UploadedFile } from "../hooks/useImageUpload";

export interface UploadDropzoneProps {
  files: UploadedFile[];
  onAdd: (incoming: FileList | File[] | null) => void;
  onRemoveAt: (index: number) => void;
  /** Cap used for the hint text and the empty-state picker UX. */
  maxFiles?: number;
  accept?: string;
  className?: string;
  disabled?: boolean;
}

const DEFAULT_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

/** Heuristic: should we ignore this paste event? Skips when the user is typing
 *  in a regular text input or textarea so Ctrl+V still pastes text normally. */
function shouldIgnorePasteTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type.toLowerCase();
    // File inputs deliberately use paste events to deliver the file, so
    // don't skip them. Everything else (text, email, search, etc.) is a
    // real text-entry target.
    return type !== "file";
  }
  return false;
}

/** Filter an incoming list to image files only. Used by input/drop/paste. */
function pickImages(incoming: FileList | File[] | null): File[] {
  if (!incoming) return [];
  return Array.from(incoming).filter((f) => f.type.startsWith("image/"));
}

// Module-level last-paste timestamp. Lives OUTSIDE the component so it
// survives React StrictMode's double-mount cycle (useRef would reset).
// Two paste listeners may briefly co-exist; this guard ensures only the
// first one to see a given event triggers the upload.
let lastPasteTimestamp = 0;

export function UploadDropzone({
  files,
  onAdd,
  onRemoveAt,
  maxFiles,
  accept = DEFAULT_ACCEPT,
  className,
  disabled,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const handleIncoming = useCallback(
    (incoming: FileList | File[] | null) => {
      const picked = pickImages(incoming);
      if (picked.length === 0) return;
      onAdd(picked);
    },
    [onAdd],
  );

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleIncoming(e.target.files);
    // Allow re-selecting the same file(s)
    e.target.value = "";
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOver(false);
    if (disabled) return;
    handleIncoming(e.dataTransfer.files);
  };

  const hasFiles = files.length > 0;
  const hitMax = typeof maxFiles === "number" && files.length >= maxFiles;

  // Global Ctrl+V / Cmd+V paste-to-upload. Listens on `document` so it
  // works regardless of which element has focus, while still respecting
  // real text-entry targets. Paste is intentionally single-image because
  // that's how OS clipboards hand images over (one image per clipboard).
  //
  // StrictMode + dev double-mount can leave TWO listeners briefly attached
  // to the document; both see the same physical event with the same
  // `timeStamp`. The module-level `lastPasteTimestamp` guard ensures only
  // the first listener triggers `onAdd` for any single paste.
  useEffect(() => {
    if (disabled) return;

    const onPaste = (event: ClipboardEvent) => {
      // De-dupe identical events arriving at two co-existing listeners.
      // Different physical pastes have monotonically increasing timestamps
      // (with realistic gaps well above 100ms), so this only blocks
      // StrictMode-induced duplicates.
      if (event.timeStamp - lastPasteTimestamp < 100) return;
      lastPasteTimestamp = event.timeStamp;

      if (!event.clipboardData) return;
      if (shouldIgnorePasteTarget(event.target)) return;
      const items = event.clipboardData.items;
      if (!items || items.length === 0) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== "file") continue;
        if (!item.type.startsWith("image/")) continue;
        const pasted = item.getAsFile();
        if (!pasted) continue;
        event.preventDefault();
        onAdd([pasted]);
        return;
      }
    };

    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, [disabled, onAdd]);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled && !hitMax) setIsOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !hitMax) setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && !hitMax && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (hitMax) return;
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        role="button"
        tabIndex={disabled || hitMax ? -1 : 0}
        aria-disabled={disabled || hitMax}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[var(--radius-card)] border-2 border-dashed p-10 text-center transition-colors",
          isOver
            ? "border-[color:var(--primary)] bg-[color:var(--surface-2)]"
            : "border-[color:var(--border)] bg-[color:var(--surface)] hover:bg-[color:var(--surface-2)]",
          (disabled || hitMax) && "pointer-events-none opacity-50",
        )}
      >
        <ImagePlus className="h-10 w-10 text-[color:var(--text-muted)]" />
        <div className="text-sm">
          <span className="font-medium text-[color:var(--text)]">
            {hasFiles ? "继续添加图片" : "点击或拖拽上传图片"}
          </span>
          <p className="mt-1 text-xs text-[color:var(--text-muted)]">
            支持 JPG / PNG / WebP / HEIC{typeof maxFiles === "number" ? `，单次最多 ${maxFiles} 张` : ""}
          </p>
        </div>
        <p className="text-[11px] italic text-[color:var(--text-muted)]/80">支持 Ctrl+V 粘贴图片</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={onInputChange}
          disabled={disabled || hitMax}
        />
      </div>

      {hasFiles && (
        <ul
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4"
          aria-label="已选择的图片"
        >
          {files.map((f, idx) => (
            <li
              key={`${f.file.name}-${idx}`}
              className="group relative overflow-hidden rounded-md border border-[color:var(--border)] bg-[color:var(--surface)]"
            >
              <img
                src={f.previewUrl}
                alt={f.file.name}
                className="block aspect-square w-full object-cover"
              />
              <div className="flex items-center justify-between gap-1 border-t border-[color:var(--border)] px-2 py-1 text-[11px] text-[color:var(--text-muted)]">
                <span className="truncate" title={f.file.name}>
                  {f.file.name}
                </span>
                <span className="shrink-0">{(f.file.size / 1024).toFixed(0)} KB</span>
              </div>
              <button
                type="button"
                onClick={() => onRemoveAt(idx)}
                disabled={disabled}
                aria-label={`移除 ${f.file.name}`}
                className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--surface)]/90 text-[color:var(--text)] shadow ring-1 ring-[color:var(--border)] hover:bg-[color:var(--surface-2)] disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
